import { AssistantMessageRole } from "../generated/django-enums";
import { getGlobalHeadersAsObject } from "../global-headers";

export interface AssistantMessage {
	id: number;
	role: AssistantMessageRole;
	text: string;
	/** API paths the assistant read for this reply, shown as chips. */
	reads: string[];
	created: string;
}

/** What the model asked to read of a page: its text outline, or a picture. */
export type ViewMode = "outline" | "picture";

/** A page the model asked to see; the panel's browser reads or photographs it. */
export interface PendingView {
	tool_use_id: string;
	route: string;
	focus: string;
	mode: ViewMode;
	/** How long an outline may be; the browser cuts it there. */
	outline_max_chars: number;
}

export interface AssistantConversation {
	id: number;
	created: string;
	updated: string;
	/** True while the assistant is still answering the last message. */
	running: boolean;
	/** True while the answer waits for the browser to photograph pending_views. */
	awaiting_view: boolean;
	pending_views: PendingView[];
	/** The most characters of page outline a message may carry. */
	outline_max_chars: number;
	messages: AssistantMessage[];
}

export interface ViewResult {
	tool_use_id: string;
	found: boolean;
	text: string;
	png: string;
	/** The page as indented text (outline mode); empty otherwise. */
	outline: string;
	/** True when the outline was cut at the pending view's outline_max_chars. */
	outline_truncated: boolean;
}

const BASE_URL = "/api/v2/assistant-conversations/";

const jsonHeaders = (): Record<string, string> => ({
	"Content-Type": "application/json",
	...getGlobalHeadersAsObject(),
});

export async function createAssistantConversation(): Promise<AssistantConversation> {
	const response = await fetch(BASE_URL, {
		method: "POST",
		headers: jsonHeaders(),
		body: JSON.stringify({}),
	});
	if (!response.ok) {
		throw new Error(`Failed to start a conversation: ${response.status}`);
	}
	return response.json();
}

export async function getAssistantConversation(
	id: number
): Promise<AssistantConversation> {
	const response = await fetch(`${BASE_URL}${id}/`, {
		method: "GET",
		headers: jsonHeaders(),
	});
	if (!response.ok) {
		throw new Error(`Failed to load the conversation: ${response.status}`);
	}
	return response.json();
}

export async function sendAssistantMessage(
	id: number,
	text: string,
	/** The Artemis page the user has open, path and query. */
	page: string,
	/** What the user sees on that page, as text; undefined when it could not be read. */
	pageOutline: string | undefined
): Promise<AssistantConversation> {
	const response = await fetch(`${BASE_URL}${id}/message/`, {
		method: "POST",
		headers: jsonHeaders(),
		body: JSON.stringify({ text, page, page_outline: pageOutline }),
	});
	if (!response.ok) {
		throw new Error(`Failed to send the message: ${response.status}`);
	}
	return response.json();
}

/** Hands the conversation to support; returns the new ticket's id. */
export async function escalateAssistantConversation(
	id: number,
	title: string,
	context: string
): Promise<number> {
	const response = await fetch(`${BASE_URL}${id}/escalate/`, {
		method: "POST",
		headers: jsonHeaders(),
		body: JSON.stringify({ title, page_context: context }),
	});
	if (!response.ok) {
		throw new Error(
			`Failed to hand the conversation to support: ${response.status}`
		);
	}
	const data: { ticket_id: number } = await response.json();
	return data.ticket_id;
}

export async function sendAssistantViewResult(
	id: number,
	result: ViewResult
): Promise<AssistantConversation> {
	const response = await fetch(`${BASE_URL}${id}/view_result/`, {
		method: "POST",
		headers: jsonHeaders(),
		body: JSON.stringify(result),
	});
	if (!response.ok) {
		throw new Error(`Failed to send the picture: ${response.status}`);
	}
	return response.json();
}
